import { nextTick } from 'vue';
import caret_method from '../mixins/caret_method';
import copy_paste_method from '../mixins/copy_paste_method';
import content_method from '../mixins/content_method';
import redo_undo_method from '../mixins/redo_undo_method';
import { useCaretStore } from '../stores/caret_store.js';
import { useContentStore } from '../stores/content_store.js';
import Content from './../classes/content.js';
import * as CONSTS from '../constants.js';
import { graphemeCount, sliceGraphemes } from '../text_segment.js';

export default {
  mixins: [caret_method, copy_paste_method, content_method, redo_undo_method],
  methods: {
    /**
     * paperにフォーカスがあっている状態のKeyDownイベントの度に呼ばれる
     * @param {KeyboardEvent} event
     *
     */
    keyDownActionAtPaper(event) {
      // IME入力中でない場合のみActionメソッドが走る
      switch (event.key) {
        case 'Enter':
          // SafariはIME確定Enterでcompositionendがkeydownより先に発火し、その中の
          // change→addConfirmEventでthis.isComposingが先にfalseへ戻ってしまうことがある。
          // keyCode 229はIME確定に関連するキー入力を示す慣習的な値のため、これも合わせて見る
          if (this.isComposing || event.isComposing || event.keyCode === 229) {
            // IME入力中のEnterキー押下を記録（BasicSentence/Hashiraコンポーネントでのみ使用）
            if (this.enterKeyPressed !== undefined) {
              this.enterKeyPressed = true;
            }
            // compositionendを伴わない仮確定Enter時、ブラウザ既定の改行挿入がVueを介さず
            // DOMを直接書き換えて変換中の絶対位置スタイルを壊すため、ここで確実に抑止する
            event.preventDefault();
            break;
          }

          this.enterAction(event);
          break;
        case 'Backspace':
          if (this.isComposing) break;

          this.backspaceAction(event);
          break;
        case 'Delete':
          if (this.isComposing) break;

          this.deleteAction(event);
          break;
        case 'Tab':
          if (this.isComposing) break;

          this.tabAction(event);
          break;
        // 押し続けられた場合に対応するため、keyUpと同様の処理をkeyDown時にも行う
        case 'ArrowLeft':
          if (this.isComposing) break;
          if (!event.shiftKey && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey))) {
            this.hashiraJumpAction('ArrowLeft');
          } else {
            this.arrowAction('ArrowLeft', event.shiftKey);
          }
          // デフォルト動作を無視
          event.preventDefault();
          break;
        case 'ArrowRight':
          if (this.isComposing) break;
          if (!event.shiftKey && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey))) {
            this.hashiraJumpAction('ArrowRight');
          } else {
            this.arrowAction('ArrowRight', event.shiftKey);
          }
          // デフォルト動作を無視
          event.preventDefault();
          break;
        case 'ArrowUp':
          if (this.isComposing) break;
          if (!event.shiftKey && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey))) {
            this.hashiraJumpAction('ArrowUp');
          } else {
            this.arrowAction('ArrowUp', event.shiftKey);
          }
          // デフォルト動作を無視
          event.preventDefault();
          break;
        case 'ArrowDown':
          if (this.isComposing) break;
          if (!event.shiftKey && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey))) {
            this.hashiraJumpAction('ArrowDown');
          } else {
            this.arrowAction('ArrowDown', event.shiftKey);
          }
          // デフォルト動作を無視
          event.preventDefault();
          break;
        default:
          this.defaultInput(event);
      }
    },

    async defaultInput(event) {
      // 特殊処理をエスケープ
      switch (event.key) {
        case 'a':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        case 'c':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        case 's':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        case 'v':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        case 'x':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        case 'y':
          if (event.ctrlKey) {
            this.yKeyAction(event);
            break;
          }
        case 'z':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) {
            this.zKeyAction(event);
            break;
          }
        case 'f':
          if ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey)) break;
        default:
          // 通常文字入力 OR Windows IME入力開始（event.key === 'Process'）の場合、選択範囲を削除する
          if ((this.isInputCharactor(event.key) || event.key === 'Process') && this.selectTo.contentID) {
            this.pushUndo();
            this.resetRedoRecords();
            // Windows IME（Process）の場合、nextTickでのカーソル再設定をスキップする
            // （compositionstart中にselectionを変更するとcompositionendが即発火するため）
            const skipCaret = event.key === 'Process';
            await this.deleteSelectedWordsAndSetCaret({ skipCaret });
          }
      }
    },

    isInputCharactor(inputKey) {
      return [
        // 通常入力
        'a',
        'b',
        'c',
        'd',
        'e',
        'f',
        'g',
        'h',
        'i',
        'j',
        'k',
        'l',
        'm',
        'n',
        'o',
        'p',
        'q',
        'r',
        's',
        't',
        'u',
        'v',
        'w',
        'x',
        'y',
        'z',
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        // 記号
        '!',
        '"',
        '#',
        '$',
        '%',
        '&',
        "'",
        '(',
        ')',
        '-',
        '=',
        '^',
        '~',
        '¥',
        '|',
        '@',
        '`',
        '[',
        '{',
        ';',
        '+',
        ':',
        '*',
        ']',
        '}',
        ',',
        '<',
        '.',
        '>',
        '_',
        '?',
        '_',
        ' ',
        // テンキー入力
        // 'Decimal', // .,
        // 'Key11', // 11
        // 'Key12', // 12
        // 'Multiply', // *
        // 'Add', // +
        // 'Clear', // クリア
        // 'Divide', // /
        // 'Subtract', // -
        // 'Separator', // 区切り文字
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
      ].includes(inputKey);
    },
    /**
     * 全体のKeyDownイベントの度に呼ばれる
     * @param {KeyboardEvent} event
     *
     */
    keyDownAction(event) {
      // フォーカスがpaper-container外（例：title input、検索input等）にある場合はデフォルト動作を許可
      const paperContainer = document.getElementById('paper-container');
      // Command+SとCommand+F以外は、paper-container外ではデフォルト動作を許可
      if (paperContainer && !paperContainer.contains(event.target)) {
        // Command+SまたはCommand+Fの場合は処理を続行
        const isCommandS =
          event.key === 's' && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey));
        const isCommandF =
          event.key === 'f' && ((event.metaKey && !event.ctrlKey) || (!event.metaKey && event.ctrlKey));
        if (!isCommandS && !isCommandF) {
          return;
        }
      }

      switch (event.key) {
        case 'a':
          this.aKeyAction(event);
          break;
        case 'c':
          this.cxKeyAction(event);
          break;
        case 's':
          this.sKeyAction(event);
          break;
        case 'v':
          this.vKeyAction(event);
          break;
        case 'x':
          this.cxKeyAction(event);
          break;
        case 'f':
          this.fKeyAction(event);
          break;
        default:
      }
    },
    /**
     * aキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    aKeyAction(event) {
      // Metaキー(ctrlかcommandのどちらか)が押されている場合保存する
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey)) return;

      let currentContent = this.contents[0];
      this.selectFrom.contentID = currentContent.uuid;
      this.selectFrom.wordIndex = 0;
      while (currentContent.next) {
        currentContent = currentContent.next;
      }
      this.selectTo.contentID = currentContent.uuid;
      this.selectTo.wordIndex = currentContent.bodyGraphemeLength;
      this.setCaretSelectionFirst();
      this.updateSelectRange();
      event.preventDefault();
    },
    /**
     * sキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    sKeyAction(event) {
      // Metaキー(ctrlかcommandのどちらか)が押されている場合保存する
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey)) return;

      this.save();
      event.preventDefault();
    },
    /**
     * fキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    async fKeyAction(event) {
      // Metaキー(ctrlかcommandのどちらか)が押されている場合保存する
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey)) return;
      event.preventDefault();
      this.isSearchable = true;
      await nextTick();
      document.getElementById('searchInput').focus();
    },
    /**
     * yキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    yKeyAction(event) {
      // ctrlが押されている場合redoする
      if (!event.ctrlKey) return;
      event.preventDefault();
      if (event.isComposing) return;
      let targetState = this.redoRecord.pop();
      if (!targetState) return;
      this.pushUndo();
      this.setRedoUndoState(targetState);
    },
    /**
     * zキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    zKeyAction(event) {
      // Metaキー(ctrlかcommandのどちらか)が押されている場合undoする
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey)) return;
      event.preventDefault();
      if (event.isComposing) return;
      let targetState = null;
      const selection = window.getSelection(); // caret位置取得用
      if (event.metaKey && event.shiftKey) {
        targetState = this.redoRecord.pop();
        if (!targetState) return;
        this.pushUndo();
      } else {
        targetState = this.undoRecord.pop();
        if (!targetState) return;
        this.pushRedo();
      }
      this.setRedoUndoState(targetState);
    },
    /**
     * redo,undoのstateを更新する際の共通処理
     * @param {} targetState
     *
     */
    setRedoUndoState(targetState) {
      this.contents.length = 0;
      let currentContent = targetState.firstContent;
      while (currentContent) {
        this.contents.push(currentContent);
        currentContent = currentContent.next;
      }
      if (targetState.selectTo.contentID) {
        this.selectFrom = targetState.selectFrom;
        this.selectTo = targetState.selectTo;
        this.setCaretSelectionFirst();
        this.updateSelectRange();
      } else {
        this.selectTo.contentID = '';
        this.selectTo.wordIndex = 0;
        this.contents.forEach((content) => {
          if (content.selectFrom != -1) {
            content.selectFrom = -1;
          }
          if (content.selectTo != -1) {
            content.selectTo = -1;
          }
        });
        nextTick(() => {
          const selection = window.getSelection();
          const range = document.createRange();
          let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${targetState.contentID}"]`);
          let nextFocusDiv = null;
          nextFocusContentElements.forEach((ele) => {
            let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(0, targetState.wordIndex - 1)}"]`);
            if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
          });
          range.setStart(nextFocusDiv, targetState.wordIndex > 0 ? 1 : 0);
          range.setEnd(nextFocusDiv, targetState.wordIndex > 0 ? 1 : 0);
          selection.removeAllRanges();
          selection.addRange(range);
        });
      }
    },
    /**
     * Enterキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    async enterAction(event) {
      // リロード前に確認モーダルを出す
      this.addConfirmEvent();
      this.pushUndo();
      this.resetRedoRecords();
      if (this.selectTo.contentID) {
        await this.deleteSelectedWordsAndSetCaret();
      } else {
        const selection = window.getSelection(); // caret位置取得用

        // カーソルがpaper（編集領域）の外にある場合は何もしない
        if (!selection.focusNode) return;

        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;

        if (!targetDiv) return;

        const offset = selection.focusOffset > 0 ? 1 : 0;
        let targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;

        if (!parentDiv?.dataset?.contentid) return;

        const targetContent = this.contentMap.get(parentDiv.dataset.contentid);
        if (!targetContent) return;
        let nextFocusContent = new Content();
        this.contents.push(nextFocusContent);
        nextFocusContent.tag_type = 'togaki';
        nextFocusContent.updateBody(sliceGraphemes(targetContent.updatingbody, targetDivIndex));
        nextFocusContent.next = targetContent.next;
        if (targetContent.next) {
          targetContent.next.previous = nextFocusContent;
        }
        targetContent.next = nextFocusContent;
        nextFocusContent.previous = targetContent;
        targetContent.updateBody(sliceGraphemes(targetContent.updatingbody, 0, targetDivIndex));
        // 分割点をまたぐスタイルのみ分割対象とする（start_index==targetDivIndexは後ろ側に収まるため除外）
        let separateStyles = targetContent.style.filter(
          (style) => style.start_index < targetDivIndex && targetDivIndex <= style.end_index,
        );
        // 分割点より後ろに収まるスタイルはnextFocusContentへ移すが、
        // nextFocusContentのインデックスはtargetDivIndex分前にずれるため合わせて調整する
        nextFocusContent.style = targetContent.style
          .filter((style) => targetDivIndex <= style.start_index)
          .map((style) => ({
            ...style,
            start_index: style.start_index - targetDivIndex,
            end_index: style.end_index - targetDivIndex,
          }));
        targetContent.style = targetContent.style.filter((style) => targetDivIndex > style.end_index);
        separateStyles.forEach((style) => {
          targetContent.style.push({
            style_type: style.style_type,
            start_index: style.start_index,
            end_index: targetDivIndex - 1,
          });
          nextFocusContent.style.push({
            style_type: style.style_type,
            start_index: 0,
            end_index: style.end_index - targetDivIndex,
          });
        });
        if (targetContent) this.refreshSearchForContent(targetContent);
        await nextTick(() => {
          const range = document.createRange();
          let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
          let nextFocusDiv = null;
          nextFocusContentElements.forEach((ele) => {
            let selectorArray = ele.querySelectorAll(`[data-index="0"]`);
            if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
          });
          range.setStart(nextFocusDiv, 0);
          range.setEnd(nextFocusDiv, 0);
          selection.removeAllRanges();
          selection.addRange(range);
        });
      }
      // デフォルト動作を無視
      event.preventDefault();
    },
    /**
     * backspaceキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    async backspaceAction(event) {
      // リロード前に確認モーダルを出す
      this.addConfirmEvent();
      this.pushUndo();
      this.resetRedoRecords();
      if (this.selectTo.contentID) {
        await this.deleteSelectedWordsAndSetCaret();
      } else {
        const selection = window.getSelection(); // caret位置取得用

        // カーソルがpaper（編集領域）の外にある場合は何もしない
        if (!selection.focusNode) return;

        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;

        if (!targetDiv) return;

        const offset = selection.focusOffset > 0 ? 1 : 0;
        let targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;
        const targetContent = this.contentMap.get(parentDiv.dataset.contentid);
        let nextFocusContent = null;
        let nextFocusOffset = 0;
        if (targetDivIndex == 0) {
          if (targetContent.tag_type == 'hashira') {
            targetContent.tag_type = 'togaki';
            nextFocusContent = targetContent;
            nextFocusOffset = 0;
            await nextTick(() => {
              const range = document.createRange();
              let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
              let nextFocusDiv = null;
              nextFocusContentElements.forEach((ele) => {
                let selectorArray = ele.querySelectorAll(`[data-index="${targetDivIndex}"]`);
                if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
              });
              range.setStart(nextFocusDiv, 0);
              range.setEnd(nextFocusDiv, 0);
              selection.removeAllRanges();
              selection.addRange(range);
            });
          } else if (!targetContent.previous) {
            event.preventDefault();
            return;
          } else {
            const previousContent = targetContent.previous;
            let checkStyleTypeArray = [];
            previousContent.style.forEach((style, index) => {
              if (style.end_index == previousContent.bodyGraphemeLength - 1)
                checkStyleTypeArray.push({
                  index: index,
                  style_type: style.style_type,
                });
            });
            targetContent.style.forEach((style) => {
              if (style.start_index == 0) {
                let findResult = checkStyleTypeArray.find((checkStyle) => checkStyle.style_type == style.style_type);
                if (findResult) {
                  previousContent.style[findResult.index].end_index =
                    style.end_index + previousContent.bodyGraphemeLength;
                  return;
                }
              }
              previousContent.style.push({
                style_type: style.style_type,
                start_index: style.start_index + previousContent.bodyGraphemeLength,
                end_index: style.end_index + previousContent.bodyGraphemeLength,
              });
            });
            targetDivIndex = Math.max(previousContent.graphemeLength - 1, 0);
            nextFocusOffset = previousContent.graphemeLength == 0 ? 0 : 1;
            previousContent.updateBody(previousContent.updatingbody + targetContent.updatingbody);
            previousContent.next = targetContent.next;
            if (targetContent.next) {
              targetContent.next.previous = previousContent;
            }
            targetContent.next = null;
            targetContent.previous = null;
            // リンクリストから切り離したコンテンツをストアからも除去
            const targetContentUUID = targetContent.uuid;
            const store = useContentStore();
            store.contents = store.contents.filter((c) => c.uuid !== targetContentUUID);
            nextFocusContent = previousContent;
            await nextTick(() => {
              const range = document.createRange();
              let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
              let nextFocusDiv = null;
              nextFocusContentElements.forEach((ele) => {
                let selectorArray = ele.querySelectorAll(`[data-index="${targetDivIndex}"]`);
                if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
              });
              range.setStart(nextFocusDiv, nextFocusOffset);
              range.setEnd(nextFocusDiv, nextFocusOffset);
              selection.removeAllRanges();
              selection.addRange(range);
            });
          }
        } else {
          nextFocusOffset = targetDivIndex == 1 ? 0 : 1;
          targetContent.updateBody(
            sliceGraphemes(targetContent.updatingbody, 0, targetDivIndex - 1) +
              sliceGraphemes(targetContent.updatingbody, targetDivIndex),
          );
          for (var i = 0; i < targetContent.style.length; i++) {
            if (
              targetContent.style[i].start_index == targetContent.style[i].end_index &&
              targetDivIndex == targetContent.style[i].end_index + 1
            ) {
              targetContent.style.splice(i, 1);
              i--;
              continue;
            }
            if (targetDivIndex <= targetContent.style[i].start_index)
              targetContent.style[i].start_index = targetContent.style[i].start_index - 1;
            if (targetDivIndex - 1 <= targetContent.style[i].end_index)
              targetContent.style[i].end_index = targetContent.style[i].end_index - 1;
            let sameRangeStyleIndex = targetContent.style.findIndex(
              (s) =>
                s.style_type == targetContent.style[i].style_type &&
                s.end_index == targetContent.style[i].start_index - 1,
            );
            if (sameRangeStyleIndex >= 0) {
              targetContent.style[i].start_index = targetContent.style[sameRangeStyleIndex].start_index;
              targetContent.style.splice(sameRangeStyleIndex, 1);
              if (sameRangeStyleIndex < i) {
                i--;
              }
            }
            sameRangeStyleIndex = targetContent.style.findIndex(
              (s) =>
                s.style_type == targetContent.style[i].style_type &&
                s.start_index == targetContent.style[i].end_index + 1,
            );
            if (sameRangeStyleIndex >= 0) {
              targetContent.style[i].end_index = targetContent.style[sameRangeStyleIndex].end_index;
              targetContent.style.splice(sameRangeStyleIndex, 1);
              if (sameRangeStyleIndex < i) {
                i--;
              }
            }
          }
          nextFocusContent = targetContent;
          await nextTick(() => {
            const range = document.createRange();
            let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
            let nextFocusDiv = null;
            nextFocusContentElements.forEach((ele) => {
              let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(targetDivIndex - 2, 0)}"]`);
              if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
            });
            range.setStart(nextFocusDiv, nextFocusOffset);
            range.setEnd(nextFocusDiv, nextFocusOffset);
            selection.removeAllRanges();
            selection.addRange(range);
          });
        }
        // 文字削除後、編集したこのコンテンツだけ検索情報を再計算する
        if (nextFocusContent) this.refreshSearchForContent(nextFocusContent);
      }
      // デフォルト動作を無視
      event.preventDefault();
    },
    /**
     * fn+BackSpaceキーおよびDeleteキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    async deleteAction(event) {
      // リロード前に確認モーダルを出す
      this.addConfirmEvent();
      this.pushUndo();
      this.resetRedoRecords();
      if (this.selectTo.contentID) {
        await this.deleteSelectedWordsAndSetCaret();
      } else {
        const selection = window.getSelection(); // caret位置取得用

        // カーソルがpaper（編集領域）の外にある場合は何もしない
        if (!selection.focusNode) return;

        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;

        if (!targetDiv) return;

        const offset = selection.focusOffset > 0 ? 1 : 0;
        let targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;
        const targetContent = this.contentMap.get(parentDiv.dataset.contentid);
        let nextFocusContent = null;
        if (targetDivIndex == targetContent.bodyGraphemeLength && !targetContent.next) {
          event.preventDefault();
          return;
        }
        if (targetDivIndex == targetContent.bodyGraphemeLength) {
          const nextContent = targetContent.next;
          let checkStyleTypeArray = [];
          targetContent.style.forEach((style, index) => {
            if (style.end_index == targetContent.bodyGraphemeLength - 1)
              checkStyleTypeArray.push({
                index: index,
                style_type: style.style_type,
              });
          });
          nextContent.style.forEach((style) => {
            if (style.start_index == 0) {
              let findResult = checkStyleTypeArray.find((checkStyle) => checkStyle.style_type == style.style_type);
              if (findResult) {
                targetContent.style[findResult.index].end_index = style.end_index + targetContent.bodyGraphemeLength;
                return;
              }
            }
            targetContent.style.push({
              style_type: style.style_type,
              start_index: style.start_index + targetContent.bodyGraphemeLength,
              end_index: style.end_index + targetContent.bodyGraphemeLength,
            });
          });

          targetContent.updateBody(targetContent.updatingbody + nextContent.updatingbody);
          targetContent.next = nextContent.next;
          if (targetContent.next) {
            targetContent.next.previous = targetContent;
          }
          nextContent.next = null;
          nextContent.previous = null;
          // リンクリストから切り離したコンテンツをストアからも除去
          const nextContentUUID = nextContent.uuid;
          const store = useContentStore();
          store.contents = store.contents.filter((c) => c.uuid !== nextContentUUID);
          nextFocusContent = targetContent;
          await nextTick(() => {
            const range = document.createRange();
            let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
            let nextFocusDiv = null;
            nextFocusContentElements.forEach((ele) => {
              let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(targetDivIndex - 1, 0)}"]`);
              if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
            });
            range.setStart(nextFocusDiv, targetDivIndex == 0 ? 0 : 1);
            range.setEnd(nextFocusDiv, targetDivIndex == 0 ? 0 : 1);
            selection.removeAllRanges();
            selection.addRange(range);
          });
        } else {
          targetContent.updateBody(
            sliceGraphemes(targetContent.updatingbody, 0, targetDivIndex) +
              sliceGraphemes(targetContent.updatingbody, targetDivIndex + 1),
          );
          for (var i = 0; i < targetContent.style.length; i++) {
            if (
              targetContent.style[i].start_index == targetContent.style[i].end_index &&
              targetDivIndex == targetContent.style[i].end_index
            ) {
              targetContent.style.splice(i, 1);
              i--;
              continue;
            }
            if (targetDivIndex < targetContent.style[i].start_index)
              targetContent.style[i].start_index = targetContent.style[i].start_index - 1;
            if (targetDivIndex <= targetContent.style[i].end_index)
              targetContent.style[i].end_index = targetContent.style[i].end_index - 1;
            let sameRangeStyleIndex = targetContent.style.findIndex(
              (s) =>
                s.style_type == targetContent.style[i].style_type &&
                s.end_index == targetContent.style[i].start_index - 1,
            );
            if (sameRangeStyleIndex >= 0) {
              targetContent.style[i].start_index = targetContent.style[sameRangeStyleIndex].start_index;
              targetContent.style.splice(sameRangeStyleIndex, 1);
              if (sameRangeStyleIndex < i) {
                i--;
              }
            }
            sameRangeStyleIndex = targetContent.style.findIndex(
              (s) =>
                s.style_type == targetContent.style[i].style_type &&
                s.start_index == targetContent.style[i].end_index + 1,
            );
            if (sameRangeStyleIndex >= 0) {
              targetContent.style[i].end_index = targetContent.style[sameRangeStyleIndex].end_index;
              targetContent.style.splice(sameRangeStyleIndex, 1);
              if (sameRangeStyleIndex < i) {
                i--;
              }
            }
          }
          if (targetContent.tag_type == 'serifu' && !targetContent.body.includes('「')) {
            targetContent.tag_type = 'togaki';
          }
          nextFocusContent = targetContent;
          await nextTick(() => {
            const range = document.createRange();
            let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
            let nextFocusDiv = null;
            nextFocusContentElements.forEach((ele) => {
              let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(targetDivIndex - 1, 0)}"]`);
              if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
            });
            range.setStart(nextFocusDiv, targetDivIndex == 0 ? 0 : 1);
            range.setEnd(nextFocusDiv, targetDivIndex == 0 ? 0 : 1);
            selection.removeAllRanges();
            selection.addRange(range);
          });
        }
        // 文字削除後、編集したこのコンテンツだけ検索情報を再計算する
        if (nextFocusContent) this.refreshSearchForContent(nextFocusContent);
      }
      // デフォルト動作を無視
      event.preventDefault();
    },
    /**
     * Tabキー押下時の処理
     * @param {KeyboardEvent} event
     *
     */
    async tabAction(event) {
      // リロード前に確認モーダルを出す
      this.addConfirmEvent();
      this.pushUndo();
      this.resetRedoRecords();
      const selection = window.getSelection(); // caret位置取得用

      // カーソルがpaper（編集領域）の外にある場合は何もしない
      if (!selection.focusNode) return;

      const targetDiv = selection.focusNode?.dataset?.contentid
        ? selection.focusNode.children[0]
        : selection.focusNode?.dataset?.index
        ? selection.focusNode
        : selection.focusNode.parentNode;

      if (!targetDiv) return;
      const offset = selection.focusOffset > 0 ? 1 : 0;
      let targetDivIndex = Number(targetDiv.dataset.index) + offset;
      const parentDiv = targetDiv.parentNode;
      const targetContent = this.contentMap.get(parentDiv.dataset.contentid);
      let nextFocusContent = null;
      if (targetDivIndex == 0) {
        nextFocusContent = targetContent;
      } else {
        nextFocusContent = new Content();
        this.contents.push(nextFocusContent);
        nextFocusContent.updateBody(sliceGraphemes(targetContent.updatingbody, targetDivIndex));
        nextFocusContent.next = targetContent.next;
        if (targetContent.next) {
          targetContent.next.previous = nextFocusContent;
        }
        targetContent.next = nextFocusContent;
        nextFocusContent.previous = targetContent;
        targetContent.updateBody(sliceGraphemes(targetContent.updatingbody, 0, targetDivIndex));
      }
      nextFocusContent.tag_type = 'hashira';
      await nextTick(() => {
        const range = document.createRange();
        let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
        let nextFocusDiv = null;
        nextFocusContentElements.forEach((ele) => {
          let selectorArray = ele.querySelectorAll(`[data-index="0"]`);
          if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
        });
        range.setStart(nextFocusDiv, 0);
        range.setEnd(nextFocusDiv, 0);
        selection.removeAllRanges();
        selection.addRange(range);
      });
      // デフォルト動作を無視
      event.preventDefault();
    },
    /**
     * 矢印キーの処理
     * @param {MouseEvent} event
     *
     */
    arrowAction(key, isShiftKey) {
      const selection = window.getSelection(); // caret位置取得用
      let targetContent = null;
      let targetDivIndex = null;
      let offset = 0;
      // selectTo.contentID が this.contents（=contentMap）に存在するときのみ選択範囲基準で処理する。
      // 過去の編集で切り離された要素を指している場合は contentMap.get が undefined を返すため、
      // その場合は selectTo をリセットして DOM のキャレット位置から再導出する。
      // （これを怠ると targetContent.updatingbody で例外となり、以降 event.preventDefault() が
      //   走らず、右矢印の範囲選択が壊れてページ更新まで復帰しなくなる）
      if (this.selectTo.contentID && this.contentMap.get(this.selectTo.contentID)) {
        targetContent = this.contentMap.get(this.selectTo.contentID);
        offset = targetContent.graphemeLength > 0 ? (this.selectTo.wordIndex == 0 ? 0 : 1) : 0;
        targetDivIndex = this.selectTo.wordIndex + offset;
      } else {
        if (this.selectTo.contentID) {
          this.selectTo.contentID = '';
          this.selectTo.wordIndex = 0;
        }
        if (!selection.focusNode) return;
        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;
        const parentDiv = targetDiv?.parentNode;
        targetContent = this.contentMap.get(parentDiv?.dataset?.contentid);
        if (!targetContent) return;
        offset = targetContent.graphemeLength > 0 ? (selection.focusOffset > 0 ? 1 : 0) : 0;
        if (targetContent.graphemeLength > 0) {
          // targetDiv が null/非Element（dataset を持たない）の場合や data-index 未設定の場合は
          // Number(...) が NaN になる。ここで例外/NaN を弾かないと以降の計算が壊れ、
          // event.preventDefault() まで到達せず keydown ハンドリングが途切れる。
          const parsedTargetDivIndex = Number(targetDiv?.dataset?.index);
          if (Number.isNaN(parsedTargetDivIndex)) return;
          targetDivIndex = parsedTargetDivIndex + offset;
        } else {
          targetDivIndex = 0;
        }
      }
      let nextFocusContent = null;
      let currentFocusIndex = 0;
      let currentLine = 0;
      let nextFocusIndex = 0;
      let nextFocusOffset = 1;
      // 今CaretがあるContentのtag_typeで場合分け
      // 今のCaretがどの高さにあるのかを特定する
      switch (targetContent.tag_type) {
        case 'togaki':
          currentFocusIndex =
            ((targetDivIndex - targetContent.countProtrusion(targetDivIndex)) % CONSTS.TOGAKI_MAX_COUNT) + 6;
          currentLine =
            Math.floor((targetDivIndex - targetContent.countProtrusion(targetDivIndex)) / CONSTS.TOGAKI_MAX_COUNT) + 1;
          if (currentFocusIndex == 6 && offset != 0) {
            currentLine = currentLine - 1;
            // 行末の場合
            // Protrusion対象の文字の場合も
            if (targetContent.countProtrusion(targetDivIndex) != targetContent.countProtrusion(targetDivIndex - 1)) {
              currentFocusIndex = CONSTS.TOGAKI_MAX_COUNT + 7;
            } else {
              currentFocusIndex = CONSTS.TOGAKI_MAX_COUNT + 6;
            }
          }
          break;
        case 'serifu':
          // Caretが1行目の場合
          if (targetDivIndex - targetContent.countProtrusion(targetDivIndex) < CONSTS.FIRST_SERIFU_MAX_COUNT) {
            currentFocusIndex = targetDivIndex;
            currentLine = 1;
            // Caretが1行目と2行目の境目の場合
          } else if (targetDivIndex - targetContent.countProtrusion(targetDivIndex) == CONSTS.FIRST_SERIFU_MAX_COUNT) {
            // 次行の先頭
            if (offset == 0) {
              currentFocusIndex = 4;
              currentLine = 2;
              // 1行目の末尾
            } else {
              currentFocusIndex = targetDivIndex;
              currentLine = 1;
            }
            // 2行目以降の場合
          } else {
            currentFocusIndex =
              ((targetDivIndex - CONSTS.FIRST_SERIFU_MAX_COUNT - targetContent.countProtrusion(targetDivIndex)) %
                CONSTS.SERIFU_MAX_COUNT) +
              4;
            currentLine =
              Math.floor(
                (targetDivIndex - CONSTS.FIRST_SERIFU_MAX_COUNT - targetContent.countProtrusion(targetDivIndex)) /
                  CONSTS.SERIFU_MAX_COUNT,
              ) + 2;
            // 行末の場合
            if (currentFocusIndex == 4 && offset != 0) {
              currentLine = currentLine - 1;
              if (targetContent.countProtrusion(targetDivIndex) != targetContent.countProtrusion(targetDivIndex - 1)) {
                currentFocusIndex = CONSTS.FIRST_SERIFU_MAX_COUNT + 1;
              } else {
                currentFocusIndex = CONSTS.FIRST_SERIFU_MAX_COUNT;
              }
            }
          }
          break;
        case 'hashira':
          currentFocusIndex =
            (targetDivIndex - targetContent.countProtrusion(targetDivIndex)) % CONSTS.HASHIRA_MAX_COUNT;
          currentLine =
            Math.floor((targetDivIndex - targetContent.countProtrusion(targetDivIndex)) / CONSTS.HASHIRA_MAX_COUNT) + 1;
          // 行末の場合
          if (currentFocusIndex == 0 && offset != 0) {
            currentLine = currentLine - 1;
            // Protrusion対象の文字の場合
            if (targetContent.countProtrusion(targetDivIndex) != targetContent.countProtrusion(targetDivIndex - 1)) {
              currentFocusIndex = CONSTS.HASHIRA_MAX_COUNT + 1;
            } else {
              currentFocusIndex = CONSTS.HASHIRA_MAX_COUNT;
            }
          }
          break;
        default:
      }
      let nextLineOffset = 0;
      switch (key) {
        case 'ArrowRight':
          // 次のフォーカス先が別のContentになる
          if (currentLine == 1) {
            if (targetContent.previous) {
              nextFocusContent = targetContent.previous;
              if (currentFocusIndex >= nextFocusContent.lastWordIndex) {
                nextFocusIndex = Math.max(0, nextFocusContent.graphemeLength - 1);
              } else {
                if (nextFocusContent.tag_type == 'togaki') {
                  nextLineOffset = 6;
                } else if (nextFocusContent.tag_type == 'serifu' && nextFocusContent.lineNum > 1) {
                  nextLineOffset = 4;
                }
                if (currentFocusIndex <= nextLineOffset) {
                  nextFocusIndex = Math.max(
                    0,
                    nextFocusContent.graphemeLength - (nextFocusContent.lastWordIndex - nextLineOffset),
                  );
                  nextFocusOffset = 0;
                } else {
                  nextFocusIndex = Math.max(
                    0,
                    nextFocusContent.graphemeLength - (nextFocusContent.lastWordIndex - currentFocusIndex) - 1,
                  );
                }
              }
            } else {
              nextFocusContent = targetContent;
              nextFocusIndex = 0;
              nextFocusOffset = 0;
            }
          } else {
            nextFocusContent = targetContent;
            if (nextFocusContent.tag_type == 'togaki') {
              nextLineOffset = 6;
            } else if (
              nextFocusContent.tag_type == 'serifu' &&
              targetDivIndex - nextFocusContent.countProtrusion(targetDivIndex) >=
                CONSTS.SERIFU_MAX_COUNT + CONSTS.FIRST_SERIFU_MAX_COUNT
            ) {
              nextLineOffset = 4;
            }
            if (currentFocusIndex == nextLineOffset) {
              nextFocusOffset = 0;
            }
            switch (nextFocusContent.tag_type) {
              case 'togaki':
                nextFocusIndex = Math.max(
                  0,
                  targetDivIndex -
                    CONSTS.TOGAKI_MAX_COUNT -
                    (nextFocusContent.countProtrusion(targetDivIndex) -
                      nextFocusContent.countProtrusion(targetDivIndex - CONSTS.TOGAKI_MAX_COUNT)) -
                    nextFocusOffset,
                );
                break;
              case 'serifu':
                nextFocusIndex = Math.max(
                  0,
                  targetDivIndex -
                    CONSTS.SERIFU_MAX_COUNT -
                    (nextFocusContent.countProtrusion(targetDivIndex) -
                      nextFocusContent.countProtrusion(targetDivIndex - CONSTS.SERIFU_MAX_COUNT)) -
                    nextFocusOffset,
                );
                break;
              case 'hashira':
                nextFocusIndex = Math.max(
                  0,
                  targetDivIndex -
                    CONSTS.HASHIRA_MAX_COUNT -
                    (nextFocusContent.countProtrusion(targetDivIndex) -
                      nextFocusContent.countProtrusion(targetDivIndex - CONSTS.HASHIRA_MAX_COUNT)) -
                    nextFocusOffset,
                );
                break;
              default:
            }
          }
          break;
        case 'ArrowLeft':
          if (currentLine == targetContent.lineNum) {
            if (targetContent.next) {
              nextFocusContent = targetContent.next;
              if (nextFocusContent.tag_type == 'togaki') {
                nextLineOffset = 6;
              }
              if (currentFocusIndex == nextLineOffset) {
                nextFocusOffset = 0;
              }
              if (currentFocusIndex > nextFocusContent.graphemeLength + nextLineOffset) {
                nextFocusIndex = Math.max(0, nextFocusContent.graphemeLength - 1);
              } else {
                if (currentFocusIndex - nextLineOffset - 1 < 0) {
                  nextFocusIndex = 0;
                  nextFocusOffset = 0;
                } else {
                  nextFocusIndex = currentFocusIndex - nextLineOffset - 1;
                }
              }
            } else {
              nextFocusContent = targetContent;
              nextFocusIndex = Math.max(0, targetContent.graphemeLength - 1);
            }
          } else {
            nextFocusContent = targetContent;
            if (nextFocusContent.tag_type == 'togaki') {
              nextLineOffset = 6;
            } else if (nextFocusContent.tag_type == 'serifu') {
              nextLineOffset = 4;
            }
            if (currentFocusIndex <= nextLineOffset) {
              nextFocusOffset = 0;
            }
            switch (nextFocusContent.tag_type) {
              case 'togaki':
                nextFocusIndex = Math.max(
                  0,
                  Math.min(
                    targetDivIndex +
                      (nextFocusContent.countProtrusion(targetDivIndex + CONSTS.TOGAKI_MAX_COUNT) -
                        nextFocusContent.countProtrusion(targetDivIndex)) +
                      CONSTS.TOGAKI_MAX_COUNT,
                    targetContent.graphemeLength,
                  ) - nextFocusOffset,
                );
                break;
              case 'serifu':
                if (targetDivIndex <= 4) {
                  nextFocusIndex =
                    CONSTS.FIRST_SERIFU_MAX_COUNT + nextFocusContent.countProtrusion(CONSTS.FIRST_SERIFU_MAX_COUNT);
                } else {
                  nextFocusIndex = Math.max(
                    0,
                    Math.min(
                      targetDivIndex +
                        (nextFocusContent.countProtrusion(targetDivIndex + CONSTS.SERIFU_MAX_COUNT) -
                          nextFocusContent.countProtrusion(targetDivIndex)) +
                        CONSTS.SERIFU_MAX_COUNT,
                      targetContent.graphemeLength,
                    ) - nextFocusOffset,
                  );
                }
                break;
              case 'hashira':
                nextFocusIndex = Math.max(
                  0,
                  Math.min(
                    targetDivIndex +
                      (nextFocusContent.countProtrusion(targetDivIndex + CONSTS.HASHIRA_MAX_COUNT) -
                        nextFocusContent.countProtrusion(targetDivIndex)) +
                      CONSTS.HASHIRA_MAX_COUNT,
                    targetContent.graphemeLength,
                  ) - nextFocusOffset,
                );
                break;
              default:
            }
          }
          break;
        case 'ArrowUp':
          if (targetDivIndex == 0) {
            if (targetContent.previous) {
              nextFocusContent = targetContent.previous;
              nextFocusIndex = Math.max(0, nextFocusContent.graphemeLength - 1);
            } else {
              return;
            }
          } else if (targetDivIndex == 1) {
            nextFocusContent = targetContent;
            nextFocusIndex = targetDivIndex - 1;
            nextFocusOffset = 0;
          } else {
            nextFocusContent = targetContent;
            nextFocusIndex = targetDivIndex - 2;
          }
          break;
        case 'ArrowDown':
          if (targetDivIndex == targetContent.graphemeLength) {
            if (targetContent.next) {
              nextFocusContent = targetContent.next;
              nextFocusIndex = 0;
              nextFocusOffset = 0;
            } else {
              return;
            }
          } else {
            nextFocusContent = targetContent;
            nextFocusIndex = targetDivIndex;
          }
          break;
        default:
      }
      // 移動先が this.contents（=contentMap）に存在しない場合、previous/next ポインタが
      // this.contents と不整合（過去の編集で切り離された要素を指す）であることを意味する。
      // このまま selectTo に設定すると、次回以降 contentMap.get が undefined を返して例外となり、
      // 右矢印の範囲選択が壊れてページ更新まで復帰しなくなるため、ここで移動を中断する。
      if (!nextFocusContent || !this.contentMap.get(nextFocusContent.uuid)) return;
      if (nextFocusContent.graphemeLength == 0) {
        nextFocusOffset = 0;
      }
      if (isShiftKey) {
        if (!this.selectTo.contentID) {
          if (key == 'ArrowRight' || key == 'ArrowUp') {
            if (targetDivIndex == 0 && targetContent.previous) {
              // content先頭の場合、前のcontentの末尾をselectFromにする
              this.selectFrom.contentID = targetContent.previous.uuid;
              this.selectFrom.wordIndex = Math.max(0, targetContent.previous.graphemeLength - 1);
            } else {
              this.selectFrom.contentID = targetContent.uuid;
              this.selectFrom.wordIndex = Math.max(0, targetDivIndex - 1);
            }
            this.selectTo.wordIndex = nextFocusIndex;
          } else if (nextFocusContent !== targetContent && targetDivIndex === targetContent.graphemeLength) {
            // content末尾から次のcontentに移動する場合、次のcontentの先頭をselectFromにする
            this.selectFrom.contentID = nextFocusContent.uuid;
            this.selectFrom.wordIndex = 0;
            this.selectTo.wordIndex = nextFocusIndex;
          } else {
            this.selectFrom.contentID = targetContent.uuid;
            this.selectFrom.wordIndex = Math.min(targetDivIndex, Math.max(0, targetContent.graphemeLength - 1));
            this.selectTo.wordIndex = nextFocusIndex;
          }
        } else {
          if (key == 'ArrowRight' || key == 'ArrowUp') {
            this.selectTo.wordIndex = nextFocusIndex;
          } else {
            // 同一Content内での行移動時のみ+1する。
            // 別Contentへ移動する場合に+1すると、1文字Contentが連続しているケースで
            // selectFrom > selectTo となり範囲が反転し、意図せず2文字分解除されてしまう
            if (targetDivIndex == 0 && nextFocusContent === targetContent) {
              this.selectTo.wordIndex = nextFocusIndex + 1;
            } else {
              this.selectTo.wordIndex = nextFocusIndex;
            }
          }
        }
        this.selectTo.contentID = nextFocusContent.uuid;
        this.setCaretSelectionFirst();
        this.updateSelectRange();
      } else {
        this.selectTo.contentID = '';
        this.selectTo.wordIndex = 0;
        this.contents.forEach((content) => {
          if (content.selectFrom != -1) {
            content.selectFrom = -1;
          }
          if (content.selectTo != -1) {
            content.selectTo = -1;
          }
        });
        const range = document.createRange();
        let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${nextFocusContent.uuid}"]`);
        let nextFocusDiv = null;
        nextFocusContentElements.forEach((ele) => {
          let selectorArray = ele.querySelectorAll(`[data-index="${nextFocusIndex}"]`);
          if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
        });
        // 移動先が仮想化により未描画（0件）または境界で重複（2件以上）の場合、
        // nextFocusDiv が null のまま range.setStart で例外が発生し、以降のカーソル移動が
        // 壊れる（右矢印が動かなくなる）ため、ここで安全に処理を中断する
        if (!nextFocusDiv) return;
        range.setStart(nextFocusDiv, nextFocusOffset);
        range.setEnd(nextFocusDiv, nextFocusOffset);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
  },
};
